import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import React, { RefObject, useRef } from 'react';
import { observer } from 'src/utils/mobx-react';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { getWindowInnerWidth } from 'src_common/common/mobx-utils/Services/window';
import { HtmlElementReactive } from 'src_common/common/mobx-utils/HtmlElementReactive';
import { MatchLeadWrapper } from 'src/domains/sportsbook/webview/components/matchLead/MatchLeadWrapper';
import { MarketFilters } from 'src/domains/sportsbook/webview/components/marketFilters/MarketFilters';
import { MarketFilters as MarketFiltersUnified } from 'src/domains/sportsbook/webview/components/marketFiltersUnified/MarketFilters';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import NFLlogo from 'src/domains/sportsbook/webview/components/eventsList/eventRowNFL/assets/Official_NFL_logo.webp';
import {
    renderRAB,
    renderViewRab,
    contentMarkets,
    renderContentMarketsRAB,
    renderViewNormal,
    renderRABBottomBetslip,
} from './MatchCardComponents';
import {
    EventsNFLLogoImage,
    EventsNFLLogoImageWrapper,
} from 'src/domains/sportsbook/webview/components/eventsTable/EventsTable.style';
import { MatchCardSection } from './MatchCard.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

const getMarketsWrapperWidth = (
    breakpointsState: BreakpointsState,
    marketsWrapperRef: RefObject<HTMLDivElement>
): number | undefined => {
    const windowWidth = getWindowInnerWidth();

    const { desktop } = breakpointsState;

    if (windowWidth === null) {
        return undefined;
    }

    if (desktop.isBiggerOrEq === false) {
        return windowWidth;
    }
    return marketsWrapperRef.current?.getBoundingClientRect().width;
};

interface MatchCardPropsType {
    eventId: EventId;
    isFooter: boolean;
}

export const MatchCard = observer('MatchCard', (props: MatchCardPropsType) => {
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const unifiedComponentConfig = ConfigComponents.get(common).unifiedComponentConfig;
    const breakpointsState = BreakpointsState.get(common);
    const { isFooter, eventId } = props;
    const rabState = appSportsBookState.rab;
    const rabId = rabState.getOrCreateRabBasket(eventId).id;
    const rabAvailable = rabState.isAvailableForEvent(eventId);
    const { isBetBuilderOpen: isYouDecideOpen } = rabState;
    const {
        componentVariants: { sportsbookVariant },
        unifiedSportEventsTable,
        multibetWidget,
    } = unifiedComponentConfig;

    const marketsWrapperRef = useRef<HTMLDivElement>(null);
    const reactiveWrapper: HtmlElementReactive<HTMLElement> = new HtmlElementReactive(500);

    const event = eventId.getEventModel();
    if (event === null) {
        return null;
    }

    const isEventStarted = event.timeSettingsStarted;

    const marketFiltersForSport = appSportsBookState.getMarketFiltersBySport(event.sport, eventId);

    const elementRefHeight = (): number | null => {
        const elementRef = reactiveWrapper.ref;
        return elementRef?.clientHeight ?? null;
    };

    const canShowBottomBetslipForInPlay = multibetWidget !== null ? false : isEventStarted === false;

    return (
        <MatchCardSection
            isFooterVisible={isFooter}
            rabBetslipHeight={elementRefHeight()}
        >
            <>
                <MatchLeadWrapper event={event} />
                {unifiedSportEventsTable === false && rabAvailable
                    ? renderRAB(rabState, eventId, sportsbookVariant)
                    : null}
            </>
            {unifiedSportEventsTable ? (
                <MarketFiltersUnified
                    marketFiltersForSport={marketFiltersForSport}
                    rabState={rabState}
                    eventId={eventId}
                />
            ) : null}
            {isYouDecideOpen === false &&
            marketFiltersForSport.isMarketFiltersAvailableForSport &&
            !unifiedSportEventsTable ? (
                <MarketFilters marketFiltersForSport={marketFiltersForSport} />
            ) : null}

            {(rabAvailable === true && isEventStarted === false) || multibetWidget !== null
                ? renderViewRab(
                      marketsWrapperRef,
                      contentMarkets(marketFiltersForSport, event, sportsbookVariant),
                      getMarketsWrapperWidth(breakpointsState, marketsWrapperRef),
                      isYouDecideOpen,
                      isYouDecideOpen === false && marketFiltersForSport.isMarketFiltersAvailableForSport,
                      rabAvailable,
                      renderContentMarketsRAB(rabId),
                      sportsbookVariant,
                      event,
                      multibetWidget !== null
                  )
                : renderViewNormal(
                      marketsWrapperRef,
                      contentMarkets(marketFiltersForSport, event, sportsbookVariant),
                      getMarketsWrapperWidth(breakpointsState, marketsWrapperRef),
                      isYouDecideOpen,
                      isYouDecideOpen === false && marketFiltersForSport.isMarketFiltersAvailableForSport,
                      rabAvailable,
                      sportsbookVariant
                  )}

            {isYouDecideOpen === true && rabAvailable === true && canShowBottomBetslipForInPlay === true
                ? renderRABBottomBetslip(reactiveWrapper, isFooter, rabId)
                : null}

            {event.sport === 'americanfootball' ? (
                <EventsNFLLogoImageWrapper>
                    <EventsNFLLogoImage
                        src={NFLlogo}
                        alt='Official NFL logo'
                    />
                </EventsNFLLogoImageWrapper>
            ) : null}
        </MatchCardSection>
    );
});
